import { useQuery } from '@tanstack/react-query';
import queryString from 'query-string';
import { apiClient } from 'lib/api';
import { isAxiosError } from 'axios';
import queryKeys from './queryKeys';
import type { QueryConfig } from 'lib/react-query';

export interface GetAccountsParams {
  params: Record<string, unknown>;
}

export interface AccountEntry {
  account_id: string;
  name: string;
}

export const getAccounts = async (
  { params }: GetAccountsParams,
  signal?: AbortSignal,
): Promise<AccountEntry[]> => {
  const res = await apiClient.get<AccountEntry[]>(queryKeys.accounts, {
    params,
    signal,
    paramsSerializer: (prms) =>
      queryString.stringify(prms, { arrayFormat: 'comma' }),
  });

  if (isAxiosError(res)) {
    const error = new Error('Get Ad Accounts Failed');
    error.cause = res.response?.data;
    return Promise.reject(error);
  }

  return res.data;
};

export interface UseAccountsOptions<TData> {
  params: Record<string, unknown>;
  config?: QueryConfig<typeof getAccounts, TData>;
}

export const useAccounts = <TData = AccountEntry[]>({ params, config }: UseAccountsOptions<TData>) => {
  return useQuery({
    staleTime: 60 * 30 * 1000,
    ...config,
    queryFn: ({ signal }) => getAccounts({ params }, signal),
    queryKey: [queryKeys.accounts, params],
  });
};
